/**
 * ClassesTab – the school's classes, with a drawer for each class's teachers
 * and students, and dialogs to create, edit and archive.
 */

import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { DataTable, type Column } from '../analytics/DataTable';
import { AnalyticsEmptyState, AnalyticsPanel, TableSkeleton } from '../analytics/AnalyticsPanel';
import type { Class } from '../../types/lms';
import type { SchoolRoster } from '../../hooks/useSchoolRoster';
import { MAX_CLASS_SIZE } from '../../services/classManagementService';
import { getSchoolById } from '../../services/schoolManagementService';
import type { UserProfile } from '../../utils/rbac';
import { ClassDrawer, classLabel } from './ClassDrawer';
import { ClassFormDialog, CurriculumRequestDialog, gradeOf } from './ClassDialogs';
import { classActivity } from '../../lib/classroom/attention';
import { formatPercent, rangeForPreset, scoreTone } from '../../lib/analytics/metrics';
import type { SchoolResults } from '../../hooks/useSchoolResults';

const SELECT = 'h-9 rounded-md border border-input bg-background px-2.5 text-sm text-foreground';

function Capacity({ count }: { count: number }) {
  const left = MAX_CLASS_SIZE - count;
  const tone = left <= 0 ? 'bg-destructive' : left <= 3 ? 'bg-amber-500' : 'bg-primary';
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-muted sm:block">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, (count / MAX_CLASS_SIZE) * 100)}%` }} />
      </div>
      <span className="tabular-nums text-foreground">
        {count}/{MAX_CLASS_SIZE}
      </span>
    </div>
  );
}

export function ClassesTab({
  roster,
  schoolId,
  profile,
  results,
  openId,
  onOpen,
}: {
  roster: SchoolRoster;
  schoolId: string;
  profile: UserProfile | null;
  results: SchoolResults;
  /** The class whose drawer is open, from the URL. */
  openId: string | null;
  onOpen: (id: string | null) => void;
}) {
  const [curriculum, setCurriculum] = useState<string | null>(null);
  const [grade, setGrade] = useState(0);
  const [subject, setSubject] = useState('');
  const [form, setForm] = useState<{ editing: Class | null } | null>(null);
  const [requestingCurriculum, setRequestingCurriculum] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSchoolById(schoolId)
      .then((s) => !cancelled && setCurriculum(s?.boardAffiliation ?? null))
      .catch(() => !cancelled && setCurriculum(null));
    return () => {
      cancelled = true;
    };
  }, [schoolId, requestingCurriculum]);

  const canRequestCurriculum = profile?.role === 'school' || profile?.role === 'principal';
  const open = openId ? roster.classById.get(openId) ?? null : null;

  const grades = useMemo(() => [...new Set(roster.classes.map((c) => gradeOf(c.class_name)).filter(Boolean))].sort((a, b) => a - b), [roster.classes]);
  const subjects = useMemo(() => [...new Set(roster.classes.map((c) => c.subject).filter(Boolean) as string[])].sort(), [roster.classes]);
  const activity = useMemo(
    () => classActivity(roster.classes.map((c) => c.id), results.results, rangeForPreset('30d'), results.passThreshold),
    [roster.classes, results.results, results.passThreshold]
  );
  const rows = roster.classes.filter((c) => (!grade || gradeOf(c.class_name) === grade) && (!subject || c.subject === subject));

  const columns: Column<Class>[] = [
    {
      key: 'name',
      header: 'Class',
      sortValue: (c) => `${String(gradeOf(c.class_name)).padStart(2, '0')} ${c.class_name} ${c.section ?? ''}`,
      cell: (c) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{classLabel(c)}</p>
          <p className="truncate text-xs text-muted-foreground">{[c.subject, c.academic_year].filter(Boolean).join(' · ')}</p>
        </div>
      ),
    },
    {
      key: 'lead',
      header: 'Class teacher',
      sortValue: (c) => (c.class_teacher_id ? roster.teacherById.get(c.class_teacher_id)?.name ?? '' : null),
      cell: (c) => {
        const lead = c.class_teacher_id ? roster.teacherById.get(c.class_teacher_id) : null;
        return lead ? <span className="text-foreground">{lead.name}</span> : <span className="text-xs text-amber-600 dark:text-amber-400">Not set</span>;
      },
    },
    {
      key: 'teachers',
      header: 'Teachers',
      align: 'right',
      hideOnMobile: true,
      sortValue: (c) => (c.teacher_ids ?? []).length,
      cell: (c) => <span className="tabular-nums text-muted-foreground">{(c.teacher_ids ?? []).length}</span>,
    },
    {
      key: 'lessons',
      header: 'Lessons (30d)',
      align: 'right',
      hideOnMobile: true,
      sortValue: (c) => (results.error ? null : activity.get(c.id)?.lessons ?? 0),
      cell: (c) =>
        results.loading ? (
          <span className="text-muted-foreground">…</span>
        ) : (
          <span className="tabular-nums text-muted-foreground">{results.error ? '—' : activity.get(c.id)?.lessons ?? 0}</span>
        ),
    },
    {
      key: 'average',
      header: 'Average (30d)',
      align: 'right',
      hideOnMobile: true,
      sortValue: (c) => activity.get(c.id)?.average ?? null,
      cell: (c) => {
        const avg = activity.get(c.id)?.average ?? null;
        return <span className={`tabular-nums ${scoreTone(avg, results.passThreshold)}`}>{results.loading ? '…' : formatPercent(avg)}</span>;
      },
    },
    {
      key: 'students',
      header: 'Students',
      align: 'right',
      sortValue: (c) => (c.student_ids ?? []).length,
      cell: (c) => <Capacity count={(c.student_ids ?? []).length} />,
    },
  ];

  return (
    <AnalyticsPanel
      title="Classes"
      description={`Curriculum: ${curriculum ?? '—'}. Open a class to manage its teachers and students.`}
      action={
        <div className="flex flex-wrap gap-2">
          {canRequestCurriculum && (
            <Button variant="outline" size="sm" onClick={() => setRequestingCurriculum(true)}>
              Request curriculum change
            </Button>
          )}
          <Button size="sm" onClick={() => setForm({ editing: null })}>
            <Plus className="mr-1 h-4 w-4" /> New class
          </Button>
        </div>
      }
    >
      {roster.loading ? (
        <TableSkeleton />
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(c) => c.id}
          initialSort={{ key: 'name', direction: 'asc' }}
          searchPlaceholder="Search classes"
          searchText={(c) => `${classLabel(c)} ${c.subject ?? ''} ${c.academic_year ?? ''}`}
          onRowClick={(c) => onOpen(c.id)}
          toolbar={
            <>
              <select className={SELECT} value={grade || ''} onChange={(e) => setGrade(Number(e.target.value) || 0)} aria-label="Filter by class">
                <option value="">All classes</option>
                {grades.map((g) => (
                  <option key={g} value={g}>
                    Class {g}
                  </option>
                ))}
              </select>
              <select className={SELECT} value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Filter by subject">
                <option value="">All subjects</option>
                {subjects.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </>
          }
          empty={
            <AnalyticsEmptyState
              compact
              title={roster.classes.length ? 'No classes match these filters' : 'No classes yet'}
              description={roster.classes.length ? 'Clear a filter to see more.' : 'Create your first class, then add its teachers and students.'}
            />
          }
        />
      )}

      {open && (
        <ClassDrawer
          key={open.id}
          cls={open}
          roster={roster}
          schoolId={schoolId}
          onEdit={() => {
            // One modal at a time: a dialog over the drawer reads as a click outside it.
            onOpen(null);
            setForm({ editing: open });
          }}
          onClose={() => onOpen(null)}
        />
      )}
      <ClassFormDialog
        open={!!form}
        editing={form?.editing ?? null}
        schoolId={schoolId}
        curriculum={curriculum}
        teachers={roster.teachers}
        canRequestCurriculum={canRequestCurriculum}
        onRequestCurriculum={() => {
          setForm(null);
          setRequestingCurriculum(true);
        }}
        onClose={() => setForm(null)}
      />
      <CurriculumRequestDialog
        open={requestingCurriculum}
        profile={profile}
        schoolId={schoolId}
        current={curriculum}
        onClose={() => setRequestingCurriculum(false)}
      />
    </AnalyticsPanel>
  );
}
